/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useMemo, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  IconButton, 
  Typography, 
  Switch, 
  TextField 
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Context
import { useMaterialUIController } from "context";

// Modal components
import MDEditModal from "components/MDEditModal";

// Icons
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import MenuIcon from "@mui/icons-material/Menu";

// Data
import { empresasInventarioMock } from "./data";

function TestDisponibles() {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller;

  // Filter companies based on the global search term
  const filteredEmpresas = useMemo(() => {
    if (!searchTerm) {
      return empresasInventarioMock;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return empresasInventarioMock.filter((empresa) => {
      // Normal search for most fields
      const standardMatch =
        (empresa.nombre && empresa.nombre.toLowerCase().includes(lowercasedSearchTerm)) ||
        (empresa.email && empresa.email.toLowerCase().includes(lowercasedSearchTerm)) ||
        (empresa.nit && empresa.nit.toLowerCase().includes(lowercasedSearchTerm)) ||
        (empresa.sector && empresa.sector.toLowerCase().includes(lowercasedSearchTerm)) ||
        (empresa.tamaño && empresa.tamaño.toLowerCase().includes(lowercasedSearchTerm)) ||
        (empresa.tipo_test && empresa.tipo_test.toLowerCase().includes(lowercasedSearchTerm));

      // Special handling for 'estado' to allow for feminine search terms like "activa"
      let estadoMatch = false;
      if (empresa.estado) {
        const estadoMasculine = empresa.estado.toLowerCase(); // e.g., "inactivo"
        const searchTermMasculine = lowercasedSearchTerm.replace(/a$/, "o"); // "inactiva" -> "inactivo"
        if (estadoMasculine.includes(searchTermMasculine)) {
          estadoMatch = true;
        }
      }

      return standardMatch || estadoMatch;
    });
  }, [searchTerm]);

  const [empresas, setEmpresas] = useState(empresasInventarioMock);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null); // No default selection
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  // Effect to update selectedEmpresa when filtered list changes
  useEffect(() => {
    // If there's no selection or the current selection is not in the filtered list
    if (!selectedEmpresa || !filteredEmpresas.find((e) => e.id === selectedEmpresa.id)) {
      setSelectedEmpresa(filteredEmpresas.length > 0 ? filteredEmpresas[0] : null);
    }
  }, [filteredEmpresas, selectedEmpresa]);

  const showNotification = (message, color = "success") => {
    setNotification({
      open: true,
      message,
      color,
    });
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  const handleSelectEmpresa = (empresa) => {
    setSelectedEmpresa(empresa);
  };

  const handleIncreaseTest = () => {
    if (selectedEmpresa) {
      setEmpresas((prevEmpresas) =>
        prevEmpresas.map((emp) =>
          emp.id === selectedEmpresa.id
            ? { ...emp, tests_disponibles: emp.tests_disponibles + 1 }
            : emp
        )
      );
      setSelectedEmpresa((prev) => ({
        ...prev,
        tests_disponibles: prev.tests_disponibles + 1,
      }));
      showNotification(`Test agregado a ${selectedEmpresa.nombre}`, "success");
    }
  };

  const handleDecreaseTest = () => {
    if (selectedEmpresa && selectedEmpresa.tests_disponibles > 0) {
      setEmpresas((prevEmpresas) =>
        prevEmpresas.map((emp) =>
          emp.id === selectedEmpresa.id
            ? { ...emp, tests_disponibles: emp.tests_disponibles - 1 }
            : emp
        )
      );
      setSelectedEmpresa((prev) => ({
        ...prev,
        tests_disponibles: prev.tests_disponibles - 1,
      }));
      showNotification(`Test removido de ${selectedEmpresa.nombre}`, "info");
    }
  };

  const handleToggleStatus = () => {
    if (selectedEmpresa) {
      const newStatus = selectedEmpresa.estado === "ACTIVO" ? "INACTIVO" : "ACTIVO";
      setEmpresas((prevEmpresas) =>
        prevEmpresas.map((emp) =>
          emp.id === selectedEmpresa.id ? { ...emp, estado: newStatus } : emp
        )
      );
      setSelectedEmpresa((prev) => ({ ...prev, estado: newStatus }));
      showNotification(
        `Empresa ${selectedEmpresa.nombre} ${newStatus === "ACTIVO" ? "activada" : "desactivada"}`,
        newStatus === "ACTIVO" ? "success" : "warning"
      );
    }
  };

  const handlePriceChange = (event) => {
    const newPrice = parseFloat(event.target.value) || 0;
    if (selectedEmpresa) {
      setEmpresas((prevEmpresas) =>
        prevEmpresas.map((emp) =>
          emp.id === selectedEmpresa.id ? { ...emp, precio_test: newPrice } : emp
        )
      );
      setSelectedEmpresa((prev) => ({ ...prev, precio_test: newPrice }));
    }
  };

  const handleEditTest = () => {
    setEditModalOpen(true);
  };

  const handleSaveEdit = (editedData) => {
    setEmpresas((prevEmpresas) =>
      prevEmpresas.map((emp) =>
        emp.id === selectedEmpresa.id
          ? {
              ...emp,
              tests_disponibles: parseInt(editedData.tests_disponibles) || 0,
              precio_test: parseInt(editedData.precio_test) || 0,
            }
          : emp
      )
    );
    setSelectedEmpresa((prev) => ({
      ...prev,
      tests_disponibles: parseInt(editedData.tests_disponibles) || 0,
      precio_test: parseInt(editedData.precio_test) || 0,
    }));
    setEditModalOpen(false);
    showNotification("Información de test actualizada correctamente", "success");
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
  };

  const editFields = [
    {
      name: "nombre",
      label: "Nombre de la Empresa",
      type: "text",
      disabled: true,
    },
    {
      name: "tipo_test",
      label: "Tipo de Test",
      type: "text",
      disabled: true,
    },
    {
      name: "tests_disponibles",
      label: "Cantidad de Tests",
      type: "number",
      required: true,
      inputProps: { min: 0 },
    },
    {
      name: "precio_test",
      label: "Precio por Test",
      type: "number",
      required: true,
      inputProps: { min: 0 },
      startAdornment: "$",
    },
  ];

  // Función para dividir el nombre de la empresa
  const formatCompanyName = (name) => {
    if (!name) return { line1: "", line2: "" };
    const parts = name.split(" ");
    return {
      line1: parts[0],
      line2: parts.slice(1).join(" "),
    };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Pruebas Disponibles - Gestión de Inventario
                </MDTypography>
              </MDBox>
              
              <MDBox pt={3} px={2} pb={3}>
                <Box sx={{ display: "flex", gap: 2, height: "500px" }}>
                  {/* Columna Izquierda: Lista de Empresas */}
                  <Box 
                    sx={{ 
                      width: "35%", 
                      backgroundColor: "white", 
                      borderRadius: "12px", 
                      border: "1px solid #e0e0e0",
                      display: "flex", 
                      flexDirection: "column" 
                    }}
                  >
                    <Box sx={{ p: 2.5, borderBottom: "1px solid #e0e0e0" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
                          <MenuIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                          Empresas
                        </Typography>
                        <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
                          Estados
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ flexGrow: 1, overflowY: "auto", p: 1 }}>
                      <Table size="small">
                        <TableBody>
                          {filteredEmpresas.map((empresa) => {
                            const { line1, line2 } = formatCompanyName(empresa.nombre);
                            return (
                              <TableRow
                                key={empresa.id}
                                onClick={() => handleSelectEmpresa(empresa)}
                                selected={selectedEmpresa && selectedEmpresa.id === empresa.id}
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": { backgroundColor: theme.palette.action.hover },
                                  "&.Mui-selected": {
                                    backgroundColor: theme.palette.action.selected,
                                    "&:hover": { backgroundColor: theme.palette.action.selectedHover },
                                  },
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                  "&:last-child td, &:last-child th": { border: 0 },
                                }}
                              >
                                <TableCell sx={{ borderBottom: "none", py: 1.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
                                    {line1}
                                  </Typography>
                                  {line2 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                      {line2}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ borderBottom: "none", textAlign: "right", verticalAlign: "middle" }}>
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{
                                      color: empresa.estado === "ACTIVO" ? theme.palette.success.dark : theme.palette.error.dark,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {empresa.estado === "ACTIVO" ? "Activa" : "Inactiva"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>

                  {/* Columna Derecha: Inventario de Tests */}
                  <Box 
                    sx={{ 
                      width: "65%", 
                      backgroundColor: "white", 
                      borderRadius: "12px", 
                      border: "1px solid #e0e0e0",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden"
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                        Inventario de Paquetes de Pruebas - {selectedEmpresa ? selectedEmpresa.nombre : "Ninguna empresa seleccionada"}
                      </Typography>
                    </Box>

                    <Box sx={{ flexGrow: 1, p: 2, overflow: "auto" }}>
                      <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                        <TableHead>
                          <TableRow sx={{ "& th": { fontWeight: "bold", backgroundColor: theme.palette.grey[100], padding: "12px 8px", fontSize: "0.875rem", borderBottom: "2px solid #e0e0e0" } }}>
                            <TableCell sx={{ width: "22%" }}>Nombre de la prueba</TableCell>
                            <TableCell sx={{ width: "20%" }}>Tipo de paquete</TableCell>
                            <TableCell align="center" sx={{ width: "20%" }}>Cantidad</TableCell>
                            <TableCell sx={{ width: "18%" }}>Precio</TableCell>
                            <TableCell align="center" sx={{ width: "20%" }}>Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow sx={{ "& td": { padding: "12px 8px", fontSize: "0.875rem", borderBottom: "1px solid #f0f0f0" } }}>
                            <TableCell sx={{ width: "22%" }}>{selectedEmpresa ? "Cuadrante H" : "-"}</TableCell>
                            <TableCell sx={{ width: "20%" }}>{selectedEmpresa ? "Prueba psicológica" : "-"}</TableCell>
                            <TableCell align="center" sx={{ width: "20%" }}>
                              {selectedEmpresa ? (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                  <IconButton
                                    onClick={handleDecreaseTest}
                                    disabled={selectedEmpresa.tests_disponibles === 0}
                                    size="small"
                                    sx={{
                                      backgroundColor: theme.palette.error.light,
                                      color: "white",
                                      "&:hover": { backgroundColor: theme.palette.error.main },
                                      width: 28,
                                      height: 28,
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography
                                    component="span"
                                    sx={{
                                      mx: 1,
                                      fontWeight: "medium",
                                      fontSize: "0.875rem",
                                      minWidth: "2ch",
                                      textAlign: "center",
                                    }}
                                  >
                                    {selectedEmpresa.tests_disponibles}
                                  </Typography>
                                  <IconButton
                                    onClick={handleIncreaseTest}
                                    size="small"
                                    sx={{
                                      backgroundColor: theme.palette.success.light,
                                      color: "white",
                                      "&:hover": { backgroundColor: theme.palette.success.main },
                                      width: 28,
                                      height: 28,
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell sx={{ width: "18%" }}>
                              {selectedEmpresa ? (
                                <TextField
                                  value={selectedEmpresa.precio_test}
                                  onChange={handlePriceChange}
                                  type="number"
                                  InputProps={{ 
                                    inputProps: { min: 0 },
                                    startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box>
                                  }}
                                  sx={{ 
                                    width: "90px",
                                    '& .MuiOutlinedInput-root': {
                                      height: '32px',
                                      fontSize: '0.875rem'
                                    }
                                  }}
                                  variant="outlined"
                                  size="small"
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ width: "20%" }}>
                              {selectedEmpresa ? (
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                  <Switch
                                    checked={selectedEmpresa.estado === "ACTIVO"}
                                    onChange={handleToggleStatus}
                                    color="success"
                                    size="small"
                                    sx={{ 
                                      '& .MuiSwitch-switchBase': { 
                                        padding: '6px' 
                                      },
                                      '& .MuiSwitch-thumb': { 
                                        width: 16, 
                                        height: 16 
                                      }
                                    }}
                                  />
                                  <IconButton 
                                    color="primary" 
                                    onClick={handleEditTest} 
                                    sx={{ color: "#FFC107", padding: "4px" }}
                                    size="small"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </Box>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modal para editar test */}
      <MDEditModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdit}
        title="Editar Información de Test"
        data={selectedEmpresa}
        fields={editFields}
      />

      {/* Notificación */}
      <MDSnackbar
        color={notification.color}
        icon="check"
        title="Gestión de Inventario"
        content={notification.message}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />

      <Footer />
    </DashboardLayout>
  );
}

export default TestDisponibles; 